import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline EMI Calculator Widget
const EMICalculatorWidget = ({ onClose }) => {
  const [amount, setAmount] = useState(50000);
  const [months, setMonths] = useState(6);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/emi-calculator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, months })
      });
      const data = await res.json();
      if (data.success) setResult(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="mt-2 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-3 border border-orange-200" data-testid="emi-calculator-widget">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-orange-700">EMI Calculator</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs">X</button>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[10px] text-gray-500">Fee Amount (INR)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="w-full px-2 py-1 text-xs border rounded-lg bg-white"
            data-testid="emi-amount-input"
          />
        </div>
        <div className="flex gap-1">
          {[3, 6, 9, 12].map(m => (
            <button
              key={m}
              onClick={() => setMonths(m)}
              className={`flex-1 py-1 text-[10px] rounded-lg font-medium ${months === m ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border'}`}
              data-testid={`emi-tenure-${m}`}
            >
              {m} mo
            </button>
          ))}
        </div>
        <button
          onClick={calculate}
          disabled={loading}
          className="w-full py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-lg"
          data-testid="emi-calculate-btn"
        >
          {loading ? 'Calculating...' : 'Calculate EMI'}
        </button>
        {result && (
          <div className="bg-white rounded-lg p-2 space-y-1" data-testid="emi-result">
            <div className="flex justify-between">
              <span className="text-[10px] text-gray-500">Monthly EMI</span>
              <span className="text-sm font-bold text-green-600">Rs {result.selected_plan.monthly_emi.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[10px] text-gray-500">Total Payable</span>
              <span className="text-[10px] font-medium">Rs {result.selected_plan.total_payable.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[10px] text-gray-500">Interest</span>
              <span className="text-[10px] text-orange-600">Rs {result.selected_plan.total_interest.toLocaleString()}</span>
            </div>
            <p className="text-[8px] text-gray-400 mt-1">*Indicative @ 15% p.a.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Action Link Icons
const ActionIcon = ({ type }) => {
  const icons = {
    calculator: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
    search: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    calendar: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    tag: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
    list: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>,
    sparkles: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
  };
  return icons[type] || icons.list;
};

// Create a global chat controller for triggering from anywhere
export const chatController = {
  openChat: null,
  sendInitialMessage: null
};

// Multi-Select Suggestion Component for Lead Capture
const MultiSelectSuggestion = ({ suggestion, onSubmit }) => {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const toggleOption = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => {
    if (selected.length === 0) return;
    const selectedLabels = suggestion.options
      .filter(opt => selected.includes(opt.id))
      .map(opt => opt.label);
    setSubmitted(true);
    onSubmit(selectedLabels.join(', '));
  };

  if (submitted) {
    return (
      <div className="mt-2 bg-green-50 rounded-xl p-3 border border-green-200" data-testid="multi-select-submitted">
        <div className="flex items-center gap-2 text-green-700 text-xs font-medium">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>आपके जवाब भेज दिए गए</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200" data-testid="multi-select-widget">
      <p className="text-xs font-semibold text-blue-700 mb-2">{suggestion.title}</p>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {suggestion.options.map(opt => (
          <label
            key={opt.id}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-xs ${
              selected.includes(opt.id)
                ? 'bg-orange-100 border border-orange-300 text-orange-800 font-medium'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`multi-select-option-${opt.id}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(opt.id)}
              onChange={() => toggleOption(opt.id)}
              className="w-4 h-4 text-orange-500 rounded border-gray-300 focus:ring-orange-500 accent-orange-500"
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
      <button
        onClick={handleSubmit}
        disabled={selected.length === 0}
        className="w-full mt-3 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-xl disabled:opacity-40 hover:from-orange-600 hover:to-amber-600 transition"
        data-testid="multi-select-submit"
      >
        भेजें ({selected.length} selected)
      </button>
    </div>
  );
};

const quickQuestions = [
  { hi: "Platform Demo", en: "Platform Demo" },
  { hi: "AI Features", en: "AI Features" },
  { hi: "Setup Time", en: "Setup Time" }
];

// ---- Public Live-Demo Slot Picker (no login/credentials, only slot booking) ----
export const DemoSlotPicker = ({ prefill = {}, sessionId, onClose }) => {
  const [step, setStep] = useState('details'); // details -> calendar -> done
  const [name, setName] = useState(prefill.name || '');
  const [phone, setPhone] = useState(prefill.phone || '');
  const [waConfirmed, setWaConfirmed] = useState(false);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  // Next ~14 weekdays (Mon–Fri) for the date chips.
  const weekdays = (() => {
    const out = [];
    const d = new Date();
    while (out.length < 10) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        const iso = d.toISOString().slice(0, 10);
        out.push({ iso, label: d.toLocaleDateString('hi-IN', { weekday: 'short', day: 'numeric', month: 'short' }) });
      }
    }
    return out;
  })();

  const fetchSlots = async (iso) => {
    setDate(iso);
    setSelectedSlot(null);
    setSlotsLoading(true);
    setSlots([]);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/demo-slots?date=${iso}`);
      const data = await res.json();
      if (data.success) setSlots(data.slots || []);
    } catch (e) { /* ignore */ }
    setSlotsLoading(false);
  };

  const goCalendar = () => {
    setError('');
    if (!name.trim()) { setError('कृपया अपना नाम बताएँ'); return; }
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { setError('कृपया सही 10-अंकों का WhatsApp नंबर दें'); return; }
    if (!waConfirmed) { setError('कृपया पुष्टि करें कि यही नंबर WhatsApp पर उपलब्ध है'); return; }
    setStep('calendar');
    if (weekdays[0]) fetchSlots(weekdays[0].iso);
  };

  const book = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/live-chat/book-demo-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.replace(/\D/g, '').slice(-10),
          whatsapp_confirmed: waConfirmed,
          date,
          start_time: selectedSlot,
          session_id: sessionId || null,
          school_name: prefill.school || prefill.schoolName || '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setConfirmation(data);
        setStep('done');
      } else {
        setError(data.detail || 'Booking नहीं हो पाई, कृपया दूसरा slot चुनें');
        if (date) fetchSlots(date);
      }
    } catch (e) {
      setError('नेटवर्क समस्या — कृपया दोबारा प्रयास करें');
    }
    setBooking(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3" data-testid="demo-slot-picker">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between">
          <div>
            <p className="text-sm font-bold">🎬 Live Demo Book करें</p>
            <p className="text-[11px] opacity-90">30 मिनट · हमारी team पूरा platform दिखाएगी</p>
          </div>
          <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none" data-testid="demo-slot-close">✕</button>
        </div>

        <div className="p-4 overflow-y-auto">
          {step === 'details' && (
            <div className="space-y-3" data-testid="demo-slot-details-step">
              <div>
                <label className="text-xs font-medium text-gray-600">आपका नाम</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="जैसे: राजेश शर्मा"
                  className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  data-testid="demo-slot-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">WhatsApp नंबर</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)}
                  inputMode="numeric" placeholder="10-अंकों का नंबर"
                  className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  data-testid="demo-slot-phone-input" />
                <p className="text-[10px] text-gray-400 mt-1">Demo confirmation व meeting link इसी नंबर पर WhatsApp से भेजे जाएँगे।</p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                <input type="checkbox" checked={waConfirmed} onChange={(e) => setWaConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-orange-500" data-testid="demo-slot-wa-confirm" />
                <span className="text-[11px] text-gray-700">हाँ, यही नंबर <b>WhatsApp पर उपलब्ध</b> है — इसी पर confirmation भेजें।</span>
              </label>
              {error && <p className="text-xs text-red-600" data-testid="demo-slot-error">{error}</p>}
              <button onClick={goCalendar}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-bold transition"
                data-testid="demo-slot-continue-btn">
                आगे बढ़ें → समय चुनें
              </button>
            </div>
          )}

          {step === 'calendar' && (
            <div className="space-y-3" data-testid="demo-slot-calendar-step">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">दिन चुनें (Mon–Fri)</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {weekdays.map((w) => (
                    <button key={w.iso} onClick={() => fetchSlots(w.iso)}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition ${date === w.iso ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                      data-testid={`demo-slot-date-${w.iso}`}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">समय चुनें (30-min slot)</p>
                {slotsLoading ? (
                  <p className="text-xs text-gray-400 py-4 text-center">slots लोड हो रहे हैं…</p>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 max-h-52 overflow-y-auto">
                    {slots.map((s) => (
                      <button key={s.start_time} disabled={!s.available}
                        onClick={() => setSelectedSlot(s.start_time)}
                        className={`py-1.5 rounded-lg text-[11px] font-medium border transition ${selectedSlot === s.start_time ? 'bg-orange-500 text-white border-orange-500' : s.available ? 'bg-white text-gray-700 border-gray-200 hover:bg-orange-50' : 'bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed line-through'}`}
                        data-testid={`demo-slot-time-${s.start_time}`}>
                        {s.start_time}
                      </button>
                    ))}
                    {slots.length === 0 && <p className="col-span-3 text-xs text-gray-400 py-3 text-center">इस दिन कोई slot उपलब्ध नहीं</p>}
                  </div>
                )}
              </div>
              {error && <p className="text-xs text-red-600" data-testid="demo-slot-error">{error}</p>}
              <div className="flex gap-2">
                <button onClick={() => setStep('details')}
                  className="px-3 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200"
                  data-testid="demo-slot-back-btn">← पीछे</button>
                <button onClick={book} disabled={!selectedSlot || booking}
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-bold disabled:opacity-40 transition"
                  data-testid="demo-slot-book-btn">
                  {booking ? 'Book हो रहा है…' : 'Demo Book करें ✅'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && confirmation && (
            <div className="text-center py-4 space-y-3" data-testid="demo-slot-done-step">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center text-2xl">✅</div>
              <p className="text-sm font-bold text-gray-800">Demo Book हो गया!</p>
              <p className="text-xs text-gray-600">🗓️ <b>{confirmation.when}</b> (IST)<br />30-min live demo</p>
              {confirmation.meet_link && (
                <a href={confirmation.meet_link} target="_blank" rel="noreferrer"
                  className="inline-block w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition"
                  data-testid="demo-slot-meet-link">
                  🔗 Google Meet link खोलें
                </a>
              )}
              <p className="text-[11px] text-gray-500">confirmation{confirmation.meet_link ? ' व Meet link' : ''} आपके WhatsApp ({phone.replace(/\D/g, '').slice(-10)}) पर भेज दी गई है। 🙏</p>
              <button onClick={onClose}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-bold transition"
                data-testid="demo-slot-finish-btn">ठीक है</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default function LiveChat({ initialFeatureContext, onChatOpen, onOpenDemo, onOpenPayment, deepLinkSessionId, forceFullScreen, dockOffset = false, loggedIn = false, pageContext = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(!!forceFullScreen);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showEMICalculator, setShowEMICalculator] = useState(null); // message index or null
  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [agentName] = useState('आयुष');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const SESSION_STORAGE_KEY = 'aayush_session_id';
  const MEMORY_STORAGE_KEY = 'aayush_lead_memory';

  // Aayush action-capable handler: one-tap navigation / form-open / support ticket
  const handleAayushAction = async (action) => {
    if (!action) return;
    if (action.key === 'support') {
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      try {
        const res = await fetch(`${API_URL}/api/live-chat/support-ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ message: lastUser?.content || 'Support needed', session_id: sessionId, page_context: pageContext?.label || null }),
        });
        const d = await res.json();
        setMessages((prev) => [...prev, { role: 'assistant', content: d.message || '✅ आपकी समस्या support team तक पहुँचा दी गई है।', timestamp: new Date() }]);
      } catch {
        setMessages((prev) => [...prev, { role: 'assistant', content: 'Support team को message भेजने में दिक्कत आई। कृपया WhatsApp +91 91831 00206 पर संपर्क करें।', timestamp: new Date() }]);
      }
      return;
    }
    if (action.route) {
      navigate(`${action.route}?aayush=${action.key}`);
      setTimeout(() => setIsOpen(false), 0);
    }
  };


  const getStoredMemory = () => {
    try {
      const raw = localStorage.getItem(MEMORY_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const persistLeadMemory = (payload = {}) => {
    try {
      const current = getStoredMemory();
      const updated = {
        ...current,
        ...(payload.lead_profile || {}),
        visitor_name: payload.lead_profile?.principal_name || current.visitor_name || '',
        visitor_phone: payload.lead_phone || current.visitor_phone || '',
        visitor_email: payload.lead_email || current.visitor_email || '',
      };
      localStorage.setItem(MEMORY_STORAGE_KEY, JSON.stringify(updated));
    } catch (err) {
      console.error('Lead memory save failed:', err);
    }
  };

  const buildCurrentPrefill = (override = {}) => {
    const memory = getStoredMemory();
    const rawStudents = override.students || memory.student_count || '';
    const normalizedStudents = (() => {
      const text = String(rawStudents).toLowerCase();
      const match = text.match(/(\d+)/);
      if (!match) return rawStudents;
      const count = Number(match[1]);
      if (count < 100) return '<100';
      if (count <= 200) return '100-200';
      if (count <= 500) return '200-500';
      if (count <= 1000) return '500-1000';
      return '1000+';
    })();

    return {
      name: override.name || memory.principal_name || memory.contact_name || memory.visitor_name || '',
      school: override.school || override.schoolName || memory.school_name || '',
      phone: override.phone || override.contactPhone || memory.visitor_phone || '',
      email: override.email || override.contactEmail || memory.visitor_email || '',
      students: normalizedStudents || '',
      message: override.message || memory.problems_described || '',
      schoolName: override.schoolName || override.school || memory.school_name || '',
      contactName: override.contactName || override.name || memory.principal_name || memory.contact_name || memory.visitor_name || '',
      contactPhone: override.contactPhone || override.phone || memory.visitor_phone || '',
      contactEmail: override.contactEmail || override.email || memory.visitor_email || '',
      suggestedPlan: override.suggestedPlan || 'Standard',
      suggestedSize: override.suggestedSize || 'small',
      startStep: override.startStep || 2,
      chatSessionId: override.chatSessionId || sessionId || localStorage.getItem(SESSION_STORAGE_KEY) || '',
      source: override.source || 'aayush_chat',
    };
  };

  const triggerSessionAction = async (endpoint, successMessage) => {
    try {
      const activeSessionId = sessionId || localStorage.getItem(SESSION_STORAGE_KEY);
      if (!activeSessionId) return;
      const res = await fetch(`${API_URL}/api/live-chat/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: activeSessionId })
      });
      const data = await res.json();
      window.alert(data.success ? successMessage : (data.detail || 'Action failed'));
    } catch (err) {
      console.error('Session action failed:', err);
      window.alert('Action failed');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start new session when chat opens
  const startSession = async (featureContext = null) => {
    try {
      const memory = getStoredMemory();
      const persistedSessionId = sessionId || localStorage.getItem(SESSION_STORAGE_KEY);
      const res = await fetch(`${API_URL}/api/live-chat/start-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: persistedSessionId,
          visitor_name: memory.principal_name || memory.contact_name || memory.visitor_name || null,
          visitor_phone: memory.visitor_phone || null,
          visitor_email: memory.visitor_email || null,
        })
      });
      const data = await res.json();
      if (data.success) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_STORAGE_KEY, data.session_id);
        persistLeadMemory(data);

        const restoredMessages = (data.messages || [{ role: 'assistant', content: data.welcome_message, timestamp: new Date().toISOString() }]).map(msg => ({
          ...msg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        }));
        if (data.payment_offer || data.resume_setup_offer) {
          restoredMessages.push({
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            payment_offer: data.payment_offer || null,
            resume_setup_offer: data.resume_setup_offer || null,
          });
        }
        setMessages(restoredMessages);
        
        // If we have feature context, send it automatically
        if (featureContext) {
          setTimeout(() => {
            sendMessageWithContext(featureContext, data.session_id);
          }, 1000);
        }
      }
    } catch (err) {
      setMessages([{ role: 'assistant', content: "नमस्ते! 🙏 मैं आपका शिक्षा मित्र 'आयुष' हूँ और मैं यहाँ आपकी सहायता के लिए उपस्थित हूँ। कृपया बताइए, मैं आपकी किस प्रकार मदद कर सकता हूँ?", timestamp: new Date() }]);
    }
  };

  const handleOpen = (featureContext = null) => {
    setIsOpen(true);
    if (onChatOpen) onChatOpen();
    
    if (!sessionId || messages.length === 0) {
      startSession(featureContext);
    } else if (featureContext) {
      sendMessageWithContext(featureContext, sessionId);
    }
  };

  // Expose open function globally
  useEffect(() => {
    chatController.openChat = handleOpen;
    chatController.sendInitialMessage = (msg) => {
      if (sessionId) {
        const userMsg = { role: 'user', content: msg, timestamp: new Date() };
        setMessages(prev => [...prev, userMsg]);
        sendMessageToAPI(msg, sessionId);
      }
    };
  }, [sessionId]);

  useEffect(() => {
    const persistedSession = localStorage.getItem(SESSION_STORAGE_KEY);
    if (persistedSession && !sessionId) {
      setSessionId(persistedSession);
    }
  }, [sessionId]);

  // Check for initial feature context from parent
  useEffect(() => {
    if (initialFeatureContext && !isOpen) {
      handleOpen(initialFeatureContext);
    }
  }, [initialFeatureContext]);

  // Deep-link resume: opened via WhatsApp "Chat with Aayush" link → resume the SAME
  // cross-channel conversation (wa-<phone>) in a full-screen, WhatsApp-like panel.
  useEffect(() => {
    if (deepLinkSessionId) {
      localStorage.setItem(SESSION_STORAGE_KEY, deepLinkSessionId);
      setSessionId(deepLinkSessionId);
      setFullScreen(true);
      setIsOpen(true);
      if (onChatOpen) onChatOpen();
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkSessionId]);

  const sendMessageWithContext = async (featureContext, sid) => {
    const contextMessage = `मुझे "${featureContext.feature}" feature के बारे में जानकारी चाहिए। (Day ${featureContext.day})`;
    
    const userMsg = { role: 'user', content: contextMessage, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: contextMessage, 
          session_id: sid,
          feature_context: featureContext 
        }),
        signal: controller.signal,
      });
      const data = await res.json();
      
      if (data.success) {
        persistLeadMemory(data);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response, 
          timestamp: new Date(),
          diksha_link: data.diksha_link || null,
          feature_data: data.feature_data || null,
          whatsapp_offer: data.whatsapp_offer || null,
          follow_up_queued: data.follow_up_queued || null,
          suggestions: data.suggestions || [],
          demo_offer: data.demo_offer || null,
          payment_offer: data.payment_offer || null,
          resume_setup_offer: data.resume_setup_offer || null,
          action: (loggedIn && data.action) ? data.action : null,
          open_demo_scheduler: data.open_demo_scheduler || false,
          human_handoff: data.human_handoff || false,
        }]);
        if (data.open_demo_scheduler) setSlotPickerOpen(true);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'क्षमा करें, अभी जवाब देने में थोड़ी दिक्कत आई। कृपया अपना सवाल दोबारा भेजें या WhatsApp पर संपर्क करें: +91 91831 00206',
          timestamp: new Date(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'नेटवर्क थोड़ा धीमा लग रहा है। कृपया अपना संदेश एक बार फिर भेजें। 🙏',
        timestamp: new Date(),
      }]);
    } finally {
      clearTimeout(timeoutId);
      setIsTyping(false);
    }
  };

  const sendMessageToAPI = async (msg, sid) => {
    setIsTyping(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, session_id: sid, page_context: pageContext?.label || null }),
        signal: controller.signal,
      });
      const data = await res.json();

      if (data.success) {
        persistLeadMemory(data);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          diksha_link: data.diksha_link || null,
          feature_data: data.feature_data || null,
          whatsapp_offer: data.whatsapp_offer || null,
          follow_up_queued: data.follow_up_queued || null,
          suggestions: data.suggestions || [],
          demo_offer: data.demo_offer || null,
          payment_offer: data.payment_offer || null,
          resume_setup_offer: data.resume_setup_offer || null,
          action: (loggedIn && data.action) ? data.action : null,
          open_demo_scheduler: data.open_demo_scheduler || false,
          human_handoff: data.human_handoff || false,
        }]);
        if (data.open_demo_scheduler) setSlotPickerOpen(true);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'क्षमा करें, अभी जवाब देने में थोड़ी दिक्कत आई। कृपया अपना सवाल दोबारा भेजें या WhatsApp पर संपर्क करें: +91 91831 00206',
          timestamp: new Date(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'नेटवर्क थोड़ा धीमा लग रहा है। कृपया अपना संदेश एक बार फिर भेजें। 🙏',
        timestamp: new Date(),
      }]);
    } finally {
      clearTimeout(timeoutId);
      setIsTyping(false);
    }
  };

  const sendMessage = () => {
    if (!inputMessage.trim()) return;
    
    const userMsg = { role: 'user', content: inputMessage, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    
    if (sessionId) {
      sendMessageToAPI(inputMessage, sessionId);
    }
    
    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <style>{`
        @keyframes aayush-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes aayush-ring { 0% { box-shadow: 0 0 0 0 rgba(249,115,22,0.6); } 70% { box-shadow: 0 0 0 14px rgba(249,115,22,0); } 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); } }
        @keyframes aayush-slide { 0% { opacity: 0; transform: translateY(10px) scale(0.9); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .aayush-float { animation: aayush-float 3s ease-in-out infinite; }
        .aayush-ring { animation: aayush-ring 2s cubic-bezier(0.66, 0.33, 0, 1) infinite; }
        .aayush-greeting { animation: aayush-slide 0.5s ease-out forwards; }
      `}</style>

      {/* Chat Button — Redesigned */}
      {!isOpen && (
        <div className={`fixed ${dockOffset ? 'bottom-24' : 'bottom-6'} right-6 z-50 flex flex-col items-end gap-3`}>
          {/* Greeting Bubble (hides after 10s on first show) */}
          <div className="aayush-greeting bg-white rounded-2xl rounded-br-sm shadow-2xl px-4 py-3 max-w-[220px] border border-orange-100 hidden md:block" data-testid="aayush-greeting-bubble">
            <div className="flex items-start gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-1.5 animate-pulse flex-shrink-0"></span>
              <div>
                <p className="text-xs font-bold text-slate-900">नमस्ते! मैं आयुष हूँ 👋</p>
                <p className="text-[11px] text-slate-600 leading-snug mt-0.5">{loggedIn ? (pageContext?.tip || 'School चलाने में कोई मदद चाहिए? Setup, Features या किसी भी काम में पूछें!') : 'कोई सवाल? Pricing, Features या Live Demo — सब कुछ पूछें!'}</p>
              </div>
            </div>
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-orange-100 rotate-45"></div>
          </div>

          {/* Main Chat Button */}
          <button
            onClick={() => handleOpen()}
            className="aayush-ring aayush-float relative w-20 h-20 bg-gradient-to-br from-orange-500 via-amber-500 to-green-500 rounded-full shadow-2xl hover:scale-105 transition-transform group border-4 border-white overflow-hidden"
            data-testid="chat-button"
            aria-label="Chat with Aayush"
          >
            {/* Avatar Icon */}
            <div className="relative w-full h-full flex items-center justify-center">
              <i className="fa-solid fa-robot text-white text-3xl group-hover:rotate-12 transition-transform"></i>
            </div>

            {/* Online dot */}
            <span className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
            </span>

            {/* "Aayush" Label Badge */}
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg whitespace-nowrap">
              AAYUSH
            </span>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={fullScreen
          ? "fixed inset-0 w-full h-full bg-white flex flex-col z-[60] overflow-hidden"
          : `fixed ${dockOffset ? 'bottom-24' : 'bottom-6'} right-6 w-[380px] h-[580px] bg-white rounded-3xl shadow-2xl flex flex-col z-50 overflow-hidden border border-slate-200 max-md:inset-0 max-md:bottom-auto max-md:right-auto max-md:w-full max-md:h-full max-md:rounded-none`} data-testid="chat-window">
          {/* Header — Theme-matching Tri-color Gradient */}
          <div className="relative bg-gradient-to-br from-orange-500 via-amber-500 to-green-600 px-5 py-4 overflow-hidden">
            {/* Decorative glow */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-yellow-300/30 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-8 -left-5 w-24 h-24 bg-green-400/20 rounded-full blur-xl"></div>

            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg border-2 border-white/60">
                    <i className="fa-solid fa-robot text-orange-500 text-xl"></i>
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                  </span>
                </div>
                <div>
                  <p className="text-white font-bold text-base flex items-center gap-1.5" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {agentName}
                    <span className="text-[10px] bg-white/25 backdrop-blur px-1.5 py-0.5 rounded-full font-semibold">AI</span>
                  </p>
                  <p className="text-white/90 text-[11px] flex items-center gap-1 font-medium">
                    <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></span>
                    शिक्षा मित्र Assistant · Online
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
              <button
                onClick={() => { setFullScreen(false); setIsOpen(false); }}
                className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white transition flex items-center justify-center"
                data-testid="chat-minimize"
                aria-label="Minimize chat"
                title="छोटा करें"
              >
                <i className="fa-solid fa-chevron-down text-lg"></i>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white transition flex items-center justify-center"
                data-testid="chat-close"
                aria-label="Close chat"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-orange-50/50 to-white">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl rounded-br-md px-4 py-3'
                    : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.feature_data && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200" data-testid="ayush-feature-images">
                      <div className="px-2 py-1 bg-gray-50 text-xs font-semibold text-gray-500">
                        {msg.feature_data.name_hi}
                      </div>
                      <div className="grid grid-cols-2 gap-0">
                        <div className="relative">
                          <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium z-10">BEFORE</div>
                          <img 
                            src={msg.feature_data.before_image} 
                            alt="Before"
                            className="w-full h-20 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <p className="text-[10px] text-gray-400 px-1 py-0.5 bg-red-50 truncate">{msg.feature_data.before_text}</p>
                        </div>
                        <div className="relative border-l border-gray-200">
                          <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium z-10">AFTER</div>
                          <img 
                            src={`${API_URL}${msg.feature_data.after_image}`}
                            alt="After - शिक्षा मित्र"
                            className="w-full h-20 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <p className="text-[10px] text-gray-400 px-1 py-0.5 bg-green-50 truncate">{msg.feature_data.after_text}</p>
                        </div>
                      </div>
                      {msg.feature_data.action_links && (
                        <div className="flex gap-1 p-1.5 bg-gray-50 border-t border-gray-100" data-testid="ayush-action-links">
                          {msg.feature_data.action_links.map((link, li) => (
                            link.type === 'tool' && link.url === 'EMI_CALCULATOR' ? (
                              <button
                                key={li}
                                onClick={() => setShowEMICalculator(showEMICalculator === idx ? null : idx)}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-[10px] font-medium hover:opacity-90 transition"
                                data-testid="emi-calculator-btn"
                              >
                                <ActionIcon type={link.icon} />
                                <span>{link.label}</span>
                              </button>
                            ) : link.type === 'tool' && link.url === 'DEMO_MODAL' ? (
                              <button
                                key={li}
                                onClick={() => setSlotPickerOpen(true)}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-[10px] font-medium hover:opacity-90 transition"
                                data-testid="chat-demo-modal-btn"
                              >
                                <ActionIcon type={link.icon} />
                                <span>{link.label}</span>
                              </button>
                            ) : link.type === 'tool' && link.url === 'PAYMENT_MODAL' ? (
                              <button
                                key={li}
                                onClick={() => onOpenPayment?.(buildCurrentPrefill({ message: msg.content }))}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-medium hover:bg-slate-800 transition"
                                data-testid="chat-payment-modal-btn"
                              >
                                <ActionIcon type={link.icon} />
                                <span>{link.label}</span>
                              </button>
                            ) : (
                              <a
                                key={li}
                                href={link.url}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-[10px] font-medium hover:bg-gray-50 transition"
                                data-testid={`action-link-${link.icon}`}
                              >
                                <ActionIcon type={link.icon} />
                                <span>{link.label}</span>
                              </a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {showEMICalculator === idx && msg.feature_data && (
                    <EMICalculatorWidget onClose={() => setShowEMICalculator(null)} />
                  )}
                  {/* Live demo now books a 30-min slot — no login/credentials. */}
                  {msg.demo_offer && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50" data-testid="aayush-demo-offer-card">
                      <div className="px-3 py-2 bg-orange-500 text-white text-xs font-bold">🎬 Live Demo Book करें</div>
                      <div className="p-3">
                        <p className="text-xs text-gray-700 mb-2">हमारी team एक 30-मिनट की online meeting पर आपको पूरा platform घुमाकर दिखाएगी। अपनी सुविधा का समय चुन लीजिए।</p>
                        <button
                          onClick={() => setSlotPickerOpen(true)}
                          className="w-full py-2 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600"
                          data-testid="aayush-demo-offer-button"
                        >
                          Live Demo Slot चुनें
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.action && msg.role === 'assistant' && (
                    <div className="mt-2" data-testid="aayush-action-card">
                      <button
                        onClick={() => handleAayushAction(msg.action)}
                        className={`w-full py-2.5 rounded-xl text-sm font-bold transition shadow-sm ${msg.action.key === 'support' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                        data-testid={`aayush-action-${msg.action.key}`}
                      >
                        {msg.action.label_hi}
                      </button>
                    </div>
                  )}
                  {msg.payment_offer && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="aayush-payment-offer-card">
                      <div className="px-3 py-2 bg-slate-900 text-white text-xs font-bold">Paid Activation / Payment</div>
                      <div className="p-3 space-y-2">
                        <p className="text-xs text-gray-700">Agar aap ready hain, to payment ke saath onboarding start ki जा सकती है. Details pre-filled rahengi.</p>
                        <div className="text-[11px] text-gray-500">
                          <div>School: <span className="font-medium text-gray-700">{msg.payment_offer.schoolName}</span></div>
                          <div>Contact: <span className="font-medium text-gray-700">{msg.payment_offer.contactName}</span></div>
                        </div>
                        <button
                          onClick={() => onOpenPayment?.(buildCurrentPrefill(msg.payment_offer))}
                          className="w-full py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800"
                          data-testid="aayush-payment-offer-button"
                        >
                          Payment ke saath Start करें
                        </button>
                        <button
                          onClick={() => triggerSessionAction('send-payment-link', 'Payment link WhatsApp par bhej diya gaya hai.')}
                          className="w-full py-2 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600"
                          data-testid="aayush-send-payment-link-button"
                        >
                          WhatsApp par Payment Link भेजें
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.resume_setup_offer && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="aayush-resume-setup-card">
                      <div className="px-3 py-2 bg-emerald-600 text-white text-xs font-bold">Resume Setup</div>
                      <div className="p-3 space-y-2">
                        <p className="text-xs text-gray-700">{msg.resume_setup_offer.schoolName} ke लिए setup pending hai. Aap login karke wahi se continue kar सकते हैं.</p>
                        <a
                          href={msg.resume_setup_offer.loginPath || '/login'}
                          className="block w-full py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold text-center hover:bg-emerald-700"
                          data-testid="aayush-resume-setup-login-button"
                        >
                          Resume Setup
                        </a>
                        <button
                          onClick={() => triggerSessionAction('send-resume-setup', 'Resume setup reminder WhatsApp par bhej diya gaya hai.')}
                          className="w-full py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800"
                          data-testid="aayush-send-resume-setup-button"
                        >
                          WhatsApp Reminder भेजें
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.whatsapp_offer && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="whatsapp-offer-card">
                      <div className="px-3 py-2 bg-green-500 text-white flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.588-1.477A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.15 0-4.14-.68-5.773-1.836l-.414-.247-4.293 1.125 1.145-4.183-.272-.432A9.71 9.71 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z"/></svg>
                        <span className="text-xs font-bold">WhatsApp EMI Offer Queued</span>
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <span>Phone:</span>
                          <span className="font-medium text-gray-800">{msg.whatsapp_offer.phone}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                            <p className="text-[10px] text-gray-500">6 months EMI</p>
                            <p className="text-sm font-bold text-green-600">Rs {msg.whatsapp_offer.emi_6_month?.toLocaleString()}/mo</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 text-center border border-green-100">
                            <p className="text-[10px] text-gray-500">12 months EMI</p>
                            <p className="text-sm font-bold text-green-600">Rs {msg.whatsapp_offer.emi_12_month?.toLocaleString()}/mo</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 rounded-lg px-2 py-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span>WhatsApp API connect होते ही auto-send होगा</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {msg.follow_up_queued && msg.role === 'assistant' && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="followup-card">
                      <div className="px-3 py-2 bg-blue-500 text-white flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                        <span className="text-xs font-bold">24hr Follow-up Scheduled</span>
                      </div>
                      <div className="p-3 space-y-1.5">
                        <p className="text-xs text-gray-700">{msg.follow_up_queued.message_preview}</p>
                        <div className="flex items-center gap-1.5 text-[10px] text-blue-600 bg-blue-50 rounded-lg px-2 py-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span>24 घंटे बाद WhatsApp reminder भेजा जाएगा</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {msg.suggestions && msg.suggestions.length > 0 && msg.role === 'assistant' && (
                    msg.suggestions.map((suggestion, si) => (
                      <MultiSelectSuggestion
                        key={si}
                        suggestion={suggestion}
                        onSubmit={(text) => {
                          const userMsg = { role: 'user', content: text, timestamp: new Date() };
                          setMessages(prev => [...prev, userMsg]);
                          sendMessageToAPI(text, sessionId);
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-2">
              {quickQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => { setInputMessage(q.hi); }}
                  className="px-3 py-1 text-xs bg-white border border-orange-200 text-orange-600 rounded-full hover:bg-orange-50 transition"
                >
                  {q.hi}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message... (Hindi/English/Hinglish)"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full flex items-center justify-center hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50"
                data-testid="chat-send-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Powered by AI • Hindi, English, Hinglish supported
            </p>
          </div>
        </div>
      )}
      {slotPickerOpen && (
        <DemoSlotPicker
          prefill={buildCurrentPrefill()}
          sessionId={sessionId || localStorage.getItem(SESSION_STORAGE_KEY)}
          onClose={() => setSlotPickerOpen(false)}
        />
      )}
    </>
  );
}
